import React, { useContext, useState } from 'react';
import Typography from '@mui/material/Typography';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ImportForm, PreviewData } from 'routes/Cellar/types';

import { useTableCreationMutation } from 'routes/ImportCsv/ImportWorkflow/hooks/tableMetadata';
import { Step } from 'routes/ImportCsv/types';
import NewTableContext from 'routes/Cellar/context/NewTable/NewTableContext';
import { useNavigate } from 'react-router-dom';
import PreVizTable from './PreVizTable';
import { dispatch } from 'store/store';

type Props = {
    previzData: PreviewData;
    setStep(step: Step): void;
    setIsLastUploadRunning(bool: boolean): void;
    useTableUpdateFile: UseMutationResult<void, Error>;
    tableColumns: ResponseColumnT[] | undefined;
    rawfile: void | File;
};

const PrevizTableStep = ({
    previzData,
    setStep,
    setIsLastUploadRunning,
    useTableUpdateFile,
    rawfile,
    tableColumns,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [importForm, setImportForm] = useState<ImportForm | null>(null);
    const queryClient = useQueryClient();
    const { isNewTable } = useContext(NewTableContext);
    const navigate = useNavigate();
    const { isPending: isTableUpdateInProgress, mutate: updateFile } =
        useTableUpdateFile;

    const onCreationError = (error: Error) => {
        dispatch.vision.notifyUser({
            message: error.message,
            snackType: 'error',
        });
        navigate(`/cellar/filesImport`);
        setStep('previzTableStep');
        setIsLastUploadRunning(false);
    };

    const tableCreationMutation = useTableCreationMutation(
        queryClient,
        // TODO: return backend name from endpoint
        (data) => {
            navigate(`/cellar/filesImport/${data.name}`);
            setStep('showTableStep');
        },
        (error) => onCreationError(error),
    );
    return (
        <>
            <Typography variant="h5">{t('importCSV.addingFile')}</Typography>
            <PreVizTable
                previzData={previzData}
                importFile={(action) => {
                    updateFile({
                        fileName: previzData.name,
                        fileData: rawfile,
                        shouldReplace: action === 'replace',
                    });
                }}
                cancel={() => {
                    const cancelStep = !isNewTable
                        ? 'showTableStep'
                        : 'selectFileStep';
                    setStep(cancelStep);
                }}
                isTableUpdateInProgress={isTableUpdateInProgress}
                isNewTable={isNewTable}
                tableColumns={tableColumns}
                importNewTableFile={(fileName) => {
                    setIsLastUploadRunning(true);
                    tableCreationMutation.mutate(
                        importForm !== null
                            ? {
                                  fileName,
                                  fileData: rawfile,
                                  ...importForm,
                              }
                            : {
                                  fileName,
                                  fileData: rawfile,
                              },
                    );
                }}
                setImportForm={setImportForm}
            />
        </>
    );
};

export default PrevizTableStep;
